/* MockProvider — in-memory DataProvider seeded from contracts/seed.json.
 * Behaves like the Go API (contracts/API.md): RBAC, validation, ids, audit, side effects.
 * Every call resolves asynchronously (optional artificial latency) and returns deep clones,
 * so callers can never mutate the mock's state by accident. */
import type { DataProvider } from "../provider";
import type * as T from "../types";
import { accountOps } from "./account";
import { aiOps } from "./ai";
import { authOps } from "./auth";
import { billingOps } from "./billing";
import { biometricOps, deviceSimulators } from "./biometric";
import { Ctx, type MockClock } from "./ctx";
import { employeesOps } from "./employees";
import { leaveOps } from "./leave";
import { lifecycleOps } from "./lifecycle";
import { Mailer } from "./mailer";
import { payrollOps } from "./payroll";
import { platformOps } from "./platform";
import { reportsOps } from "./reports";
import { requestsOps } from "./requests";
import { sitesOps } from "./sites";
import { SEED, type MockState, type SeedFile } from "./state";

/** Every DataProvider method implemented by the mock modules (async ones). */
export type MockOps = Omit<DataProvider, "mode" | "setToken" | "fileUrl">;
export type DeviceSimulators = ReturnType<typeof deviceSimulators>;

export interface MockProviderOptions {
  /** Injectable clock; defaults to seed.today / seed.now. */
  clock?: Partial<MockClock>;
  /** Artificial latency per call in ms (default 0; the app uses ~120). */
  latencyMs?: number;
  /** Alternative seed (defaults to contracts/seed.json). */
  seed?: SeedFile;
  /** Initial bearer token (e.g. restored from localStorage). */
  token?: string | null;
  /** Wall clock in ms for token expiry / rate limits (tests). */
  wallNow?: () => number;
  /** Base URL for links in emails (default: the page origin). */
  baseUrl?: string;
}

/** A captured email for the developer mailbox (mock mode only). */
export interface DevMail { id: string; to: string; subject: string; template: string; status: string; createdAt: string; lang: string; text: string; html: string; links: string[] }

function cloneArg(a: unknown): unknown {
  if (a == null || typeof a !== "object") return a;
  if (typeof Blob !== "undefined" && a instanceof Blob) return a;
  try { return structuredClone(a); } catch { return a; }
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export interface MockProvider extends MockOps, DeviceSimulators {}
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export class MockProvider implements DataProvider {
  readonly mode = "mock" as const;
  latencyMs: number;
  private readonly ctx: Ctx;

  constructor(opts: MockProviderOptions = {}) {
    this.ctx = new Ctx(opts.seed ?? SEED, opts.clock);
    this.ctx.mail = new Mailer(this.ctx);
    if (opts.wallNow) this.ctx.wallNow = opts.wallNow;
    if (opts.baseUrl) this.ctx.baseUrl = opts.baseUrl;
    this.ctx.token = opts.token ?? null;
    this.latencyMs = opts.latencyMs ?? 0;
    const c = this.ctx;
    const ops: MockOps = {
      ...authOps(c), ...employeesOps(c), ...payrollOps(c), ...leaveOps(c), ...biometricOps(c), ...requestsOps(c), ...sitesOps(c),
      ...lifecycleOps(c), ...billingOps(c), ...aiOps(c), ...reportsOps(c), ...accountOps(c), ...platformOps(c),
    };
    const all: Record<string, (...args: unknown[]) => Promise<unknown>> = { ...(ops as unknown as Record<string, (...args: unknown[]) => Promise<unknown>>), ...(deviceSimulators(c) as unknown as Record<string, (...args: unknown[]) => Promise<unknown>>) };
    const self = this as unknown as Record<string, unknown>;
    for (const [name, fn] of Object.entries(all)) {
      self[name] = async (...args: unknown[]) => {
        if (this.latencyMs > 0) await new Promise((r) => setTimeout(r, this.latencyMs));
        const out = await fn(...args.map(cloneArg));
        return out === undefined ? undefined : structuredClone(out);
      };
    }
  }

  setToken(token: string | null): void { this.ctx.token = token; }
  getToken(): string | null { return this.ctx.token; }

  fileUrl(key: string): string {
    const f = this.ctx.state.files.get(key);
    return f ? f.ref.url : "mock://" + key;
  }

  /** Blob stored under a file key (uploads, WPS exports, letters). */
  fileBlob(key: string): Blob | undefined { return this.ctx.state.files.get(key)?.blob; }

  /** Text content of a stored file (works with jsdom Blobs that lack .text()). */
  async fileText(key: string): Promise<string | undefined> {
    const b = this.fileBlob(key);
    if (!b) return undefined;
    if (typeof b.text === "function") return b.text();
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => reject(r.error);
      r.readAsText(b);
    });
  }

  /** Restore the seed state and clear the session. */
  reset(): void { this.ctx.reset(); }

  /** Developer mailbox: every email the mock "sent", newest first, with the links found in it (mock mode only). */
  devMailbox(): DevMail[] {
    return this.ctx.root.outbox.slice().reverse().map((m) => ({
      id: m.id, to: m.redirectedFrom ?? m.to, subject: m.subject, template: m.template, status: m.status, createdAt: m.createdAt, lang: m.lang, text: m.text, html: m.html,
      links: Array.from(new Set(m.text.match(/https?:\/\/[^\s<>"]+/g) ?? [])).filter((l) => !l.includes("/unsubscribe")),
    }));
  }
  /** Empty the developer mailbox. */
  clearMailbox(): void { this.ctx.root.outbox.length = 0; }
  /** Change the wall clock used for token expiry and rate limits (tests). */
  setWallNow(fn: () => number): void { this.ctx.wallNow = fn; }
  /** Unsubscribe link handling of the mock (the real one is a server page). */
  unsubscribe(email: string): T.Suppression {
    const sp: T.Suppression = { email: email.toLowerCase(), reason: "unsubscribe", note: "recipient unsubscribed", createdAt: new Date(this.ctx.wallNow()).toISOString() };
    this.ctx.root.suppressions[sp.email] = sp;
    return structuredClone(sp);
  }

  /** The injected clock (synchronous; `clock()` is the async DataProvider read of GET /clock). */
  get clockState(): MockClock { return { ...this.ctx.clock }; }
  setClock(clock: Partial<MockClock>): void { this.ctx.clock = Ctx.mkClock(this.ctx.seed, clock); }

  /** Read-only snapshot of the internal state (debugging / tests). */
  snapshot(): MockState { return this.ctx.state; }
}

export type { MockClock };
